import { useRef, useState } from 'react';
import { AssignmentRecordingItem } from '../../../../constants/types/lms/assignment/type';
import TextWinds from '../../../common/TextWinds';
import {
  ArrowDownTrayIcon,
  PauseIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import {
  formatToKstDate,
  getErrorMessages,
} from '../../../../api/utils/util-func';
import { useAppDispatch } from '../../../../store/hook';
import { openAlert } from '../../../../store/features/modalSlice';
import { getAssignmentRecordingDownloadUrl } from '../../../../api/utils/lms/assignment/api';

interface RecordingPanelProps {
  studentName: string;
  assignmentTitle: string;
  recordingListByContentSeq: {
    contentSeq: number;
    recording: AssignmentRecordingItem;
  }[];
}

/**
 * 개편 LMS (교사) - 과제 관리 > 과제 현황 페이지 > 반별 과제 현황 > 반별 과제 카드 > 학생 과제 목록 > 학생 과제 행 > 녹음 판넬
 * 개편 LMS (교사) - 과제 관리 > 과제 현황 페이지 > 학생별 과제 현황 > 학생별 과제 카드 > 녹음 판넬
 */
const RecordingPanel = ({
  studentName,
  assignmentTitle,
  recordingListByContentSeq,
}: RecordingPanelProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectedRecording = recordingListByContentSeq[selectedIndex]?.recording;

  if (!selectedRecording) {
    return null;
  }

  const handleRecordingButtonClick = (index: number) => {
    const recording = recordingListByContentSeq[index]?.recording;

    if (!recording) return;

    setSelectedIndex(index);
  };

  return (
    <div className="bg-purple1/60 px-4 py-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {recordingListByContentSeq.map(({ contentSeq, recording }, index) => {
          const isActive = selectedIndex === index;

          return (
            <button
              key={contentSeq}
              type="button"
              className={[
                'rounded-lg border px-3 py-1.5 text-xs font-bold transition-colors',
                isActive
                  ? 'border-purple5 bg-purple5 text-white'
                  : 'border-purple2 bg-white text-purple5 hover:bg-purple1',
              ].join(' ')}
              onClick={() => handleRecordingButtonClick(index)}
            >
              {recording.contentName ?? `${contentSeq}번`}
            </button>
          );
        })}
      </div>

      <RecordingPlayerCard
        key={selectedRecording.recordingId}
        studentName={studentName}
        assignmentTitle={assignmentTitle}
        recording={selectedRecording}
      />
    </div>
  );
};

export default RecordingPanel;

interface RecordingPlayerCardProps {
  studentName: string;
  assignmentTitle: string;
  recording: AssignmentRecordingItem;
}

const RecordingPlayerCard = ({
  studentName,
  assignmentTitle,
  recording,
}: RecordingPlayerCardProps) => {
  const dispatch = useAppDispatch();

  const audioRef = useRef<HTMLAudioElement | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const progressRate = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handlePlayToggle = async () => {
    const audio = audioRef.current;

    if (!audio) return;

    if (audio.paused) {
      await audio.play();
      setIsPlaying(true);
      return;
    }

    audio.pause();
    setIsPlaying(false);
  };

  const handleDownloadRecording = async (
    recording: AssignmentRecordingItem,
  ) => {
    try {
      const response = await getAssignmentRecordingDownloadUrl({
        recordingId: recording.recordingId,
      });

      const { downloadUrl, fileName } = response;

      const link = document.createElement('a');

      link.href = downloadUrl;
      link.download = fileName;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    }
  };

  const formatAudioTime = (time: number) => {
    if (!Number.isFinite(time)) return '0:00';

    const minute = Math.floor(time / 60);
    const second = Math.floor(time % 60)
      .toString()
      .padStart(2, '0');

    return `${minute}:${second}`;
  };

  return (
    <div className="rounded-xl border border-purple2 bg-white px-4 py-4">
      <audio
        ref={audioRef}
        src={recording.s3Url}
        onLoadedMetadata={(event) => {
          setDuration(event.currentTarget.duration);
        }}
        onTimeUpdate={(event) => {
          setCurrentTime(event.currentTarget.currentTime);
        }}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentTime(duration);
        }}
      />

      <div className="flex items-center gap-4">
        <button
          type="button"
          className={[
            'flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-colors',
            isPlaying
              ? 'bg-purple5 text-white'
              : 'bg-purple1 text-purple5 hover:bg-purple2',
          ].join(' ')}
          onClick={handlePlayToggle}
        >
          {isPlaying ? (
            <PauseIcon className="h-6 w-6" />
          ) : (
            <PlayIcon className="ml-0.5 h-6 w-6" />
          )}
        </button>

        <div className="min-w-0 flex-1">
          <TextWinds
            type="content_body2"
            className="block truncate font-bold text-purple5"
          >
            {studentName} · {assignmentTitle}
          </TextWinds>

          <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray1">
            <div
              className="h-full rounded-full bg-purple5 transition-[width] duration-100 ease-linear"
              style={{ width: `${progressRate}%` }}
            />
          </div>

          <TextWinds type="caption1" color="gray6" className="mt-2 block">
            제출일 {formatToKstDate(recording.createdAt, 'YYYY.MM.DD HH:mm')}
          </TextWinds>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <TextWinds type="content_body3" color="gray8" className="font-bold">
            {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
          </TextWinds>

          <button
            type="button"
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray3 bg-white text-gray6 transition-colors hover:border-purple3 hover:bg-purple1 hover:text-purple5"
            onClick={() => handleDownloadRecording(recording)}
            title="녹음 파일 다운로드"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
